import { pascalCase } from 'change-case';
import groq from 'groq';
import { DateTime } from 'luxon';

import { parseRecipe } from '../../dtos/own/recipeHelpers';
import { SanityFetcherService } from '../../services/SanityService/SanityFetcher';
import { removeNestedPropertyFromObject } from '../../utils/helpers';
import { SemboMarket, supportedLanguages } from '../../utils/markets';
import { IngredientQl, RecipeQl } from '../smart-search/hooks';
import {
  CMSTripUnparsedRecipe,
  ContentByIdMap,
  ContentByPropertyMap,
  RecipePreferences,
  SanityUUID,
} from './types';

export const convertEntriesToMappedListsByProperty = <
  T extends { [key: string]: any },
>(
  entries: T[],
  property: keyof T
) =>
  entries.reduce(
    (acc, entry) => {
      const key = entry[property];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(entry);
      return acc;
    },
    {} as ContentByPropertyMap<T, keyof T>
  );

export const convertEntriesToMapById = <T extends { _id: SanityUUID }>(
  entries: T[]
) =>
  entries.reduce((acc, entry) => {
    acc[entry._id] = entry;
    return acc;
  }, {} as ContentByIdMap<T>);

export const ingredientIsFlightType = (ingredient: IngredientQl) =>
  (ingredient?.fromIata !== undefined || ingredient?.toIata !== undefined) &&
  ((ingredient?.transportTypes?.length === 1 &&
    ingredient?.transportTypes[0]?.toUpperCase() === 'FLIGHT') ||
    ingredient?.ingredientType?.toUpperCase() === 'FLIGHT');

const isDateTooClose = (
  now: DateTime,
  targetMonth: DateTime,
  minimumDays: number,
  minimumDaysLeftInMonth?: number
) => {
  if (typeof minimumDaysLeftInMonth === 'number') {
    const daysLeftInMonth = targetMonth
      .endOf('month')
      .diff(targetMonth, 'days').days;
    if (daysLeftInMonth < minimumDaysLeftInMonth) {
      return true;
    }
  }

  const dateAfterMinimumDays = now.plus({ days: minimumDays });

  return now.startOf('month') < targetMonth.startOf('month')
    ? dateAfterMinimumDays > targetMonth.startOf('month')
    : dateAfterMinimumDays > targetMonth;
};

const serializeStartAndEndDateObjects = (targetMonth: DateTime) => ({
  startDate: targetMonth.startOf('month').toISODate() + 'T00:00:00.000Z',
  endDate: targetMonth.endOf('month').toISODate() + 'T23:59:59.000Z',
});
const handlePreferredMonthsMode = (
  preferences: RecipePreferences & { mode: 'preferredMonths' },
  now: DateTime,
  minimumDays: number
) => {
  if (preferences.preferredMonths.length === 0) {
    return { startDate: null, endDate: null };
  }

  const sortedMonths = preferences.preferredMonths
    .map((month) => now.set({ month }))
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));

  let targetMonthIndex = sortedMonths.findIndex(
    (m) => now.startOf('month') <= m.startOf('month')
  );

  // If no future month is found, take first month
  if (targetMonthIndex === -1) {
    targetMonthIndex = 0;
  }

  let targetMonth = sortedMonths[targetMonthIndex]!;

  let cyclesCompleted = 0;
  while (isDateTooClose(now, targetMonth, minimumDays)) {
    targetMonthIndex += 1;

    if (targetMonthIndex >= sortedMonths.length) {
      // Move to next year
      targetMonthIndex = 0;
      cyclesCompleted += 1;
    }

    targetMonth = sortedMonths[targetMonthIndex]!.plus({
      years: cyclesCompleted,
    });
  }

  return serializeStartAndEndDateObjects(targetMonth);
};

const handleMonthsFromNowMode = (
  preferences: RecipePreferences & { mode: 'monthsFromNow' },
  now: DateTime,
  minimumDays: number
) => {
  let targetMonth = now.plus({
    months: preferences.monthsFromNow,
    days: preferences.daysFromNow,
  });

  while (
    isDateTooClose(
      now,
      targetMonth,
      minimumDays,
      preferences.minimumDaysLeftInMonth
    )
  ) {
    targetMonth = targetMonth.startOf('month').plus({ months: 1 });
  }

  return serializeStartAndEndDateObjects(targetMonth);
};

const handleSpecificDatesMode = (
  preferences: RecipePreferences & { mode: 'specificDates' }
) => {
  if (
    !preferences.specificDates?.firstArrivalDate ||
    !preferences.specificDates?.lastArrivalDate
  ) {
    return { startDate: null, endDate: null };
  }

  const startDate =
    DateTime.fromISO(preferences.specificDates.firstArrivalDate)
      .startOf('day')
      .toISODate() + 'T00:00:00.000Z';

  const endDate =
    DateTime.fromISO(preferences.specificDates.lastArrivalDate)
      .endOf('day')
      .toISODate() + 'T23:59:59.999Z';

  return {
    startDate,
    endDate,
  };
};

export const getStartAndEndDateFromRecipePreferences = (
  preferences: RecipePreferences
): { startDate: string | null; endDate: string | null } => {
  const now = DateTime.now();
  const minimumDays = preferences.minimumDaysFromNow ?? 0;

  if (preferences.mode === 'preferredMonths') {
    return handlePreferredMonthsMode(preferences, now, minimumDays);
  } else if (preferences.mode === 'monthsFromNow') {
    return handleMonthsFromNowMode(preferences, now, minimumDays);
  } else if (preferences.mode === 'specificDates') {
    return handleSpecificDatesMode(preferences);
  } else {
    throw new Error('invalid mode');
  }
};

export const removeNestedSanityKeyPropertyFromRecipe = (
  recipe: RecipeQl
): RecipeQl => {
  return removeNestedPropertyFromObject(recipe, '_key');
};

export const getDynamicRecipe = (
  market: SemboMarket,
  trip: CMSTripUnparsedRecipe
) => {
  let recipe = parseRecipe(trip.recipe);

  const startingIATA = trip.startingIATA;

  delete recipe.returnEmptyIngredientResults;

  // delete any initial fromDate/toDate
  delete recipe.startDate;
  delete recipe.endDate;
  delete recipe.startDateOnly;
  delete recipe.endDateOnly;

  if (recipe.affiliateId) {
    delete recipe.affiliateId;
  }

  recipe = removeNestedSanityKeyPropertyFromRecipe(recipe);

  if (trip.recipePreferences) {
    const { startDate, endDate } = getStartAndEndDateFromRecipePreferences(
      trip.recipePreferences
    );

    if (startDate && endDate) {
      // set to any because the typing is mismatching from GraphQL
      recipe.startDate = startDate as any;
      recipe.endDate = endDate as any;
    }
  }

  recipe.market = `https://${market.host}`;

  if (startingIATA && recipe.ingredients?.length) {
    // modify first ingredient if it's a flight
    const firstIngredient = recipe.ingredients[0];
    if (
      firstIngredient?.fromIata !== undefined &&
      ingredientIsFlightType(firstIngredient)
    ) {
      recipe.ingredients = [
        { ...firstIngredient, fromIata: startingIATA },
        ...recipe.ingredients.slice(1),
      ];
    }

    // modify last ingredient if it's a flight
    const lastIndex = recipe.ingredients.length - 1;
    const lastIngredient = recipe.ingredients[lastIndex];
    if (
      lastIngredient?.toIata !== undefined &&
      ingredientIsFlightType(lastIngredient)
    ) {
      recipe.ingredients = [
        ...recipe.ingredients.slice(0, lastIndex),
        { ...lastIngredient, toIata: startingIATA },
      ];
    }
  }

  return recipe;
};

export const validatePreviewImages = async (_id?: string, mediaId?: string) => {
  if (_id === undefined || mediaId === undefined) {
    // no data, nothing in need of validation
    return true;
  }

  const query = groq`
  array::unique(
    *[_type == "article" && _id == "${_id}"][0]{
      "content": [
        ${supportedLanguages
          .map(
            (lang) =>
              `...content${pascalCase(
                lang
              )}->description[_type == "media" && _ref != null]._ref`
          )
          .join(',\n')}
      ]
    }.content
    )
  `;
  const queryUrl = query;

  const result = await SanityFetcherService.fetchAndLog<string[]>(queryUrl, {
    noThrottle: true,
  });

  if (result === undefined) {
    // no data, nothing in need of validation
    return true;
  }

  const isIdInArticleDescription = result.includes(mediaId);

  return isIdInArticleDescription
    ? true
    : `Preview Image must be inside the article description.`;
};
